import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Form } from 'react-final-form';
import { Card, Button, Text, Flex, Box, Spinner } from '../../design-system';
import { FaArrowCircleLeft } from 'react-icons/fa';
import {
  required,
  prettyPhone,
  composeValidators,
  phoneNumberVal,
} from '../../helpers/utils';
import Alert from '../../helpers/alert';
import FieldColumn from '../../components/FieldColumn';
import { Session, ACTIONS } from '../../contexts/sessionContext';
import { ERR_MSG, COUNTRY_OPTIONS, STATE_OPTIONS } from '../../globalConstants';

interface CreditCardDetailsProps {
  initialValues?: any;
  onSubmit: (values: any) => void;
  onBackClick: () => void;
  intent?: any;
}

const FormContainer = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const UserCard = styled(Card)`
  min-width: 600px;
`;

const BackButton = styled(Button)`
  font-size: 30px;
  padding: 0;
`;

const CardElementContainer = styled(Box)`
  border-bottom: 1px solid lightgray;
`;

const ErrorMessage = styled(Text)`
  display: block;
  margin: 4px 0px 10px 4px;
  color: ${(p) => p.theme.colors.error};
`;

const FinePrint = styled(Text)`
  color: ${(p) => p.theme.colors.grey};
`;

const cardElementStyles = {
  base: {
    fontSize: '18px',
    letterSpacing: '0.025em',
    fontFamily: 'Source Code Pro, monospace',
    '::placeholder': {
      color: '#aab7c4',
    },
  },
};

const CreditCardDetails = ({
  initialValues,
  onSubmit,
  onBackClick,
  intent,
}: CreditCardDetailsProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    state: { loading },
    dispatch,
  } = useContext(Session);
  const [stripeError, setStripeError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleOnSubmit = async (values: any) => {
    dispatch({ type: ACTIONS.REGISTERING_USER });
    setStripeError(null);

    if (!intent || !intent.client_secret) {
      setStripeError('Payment setup intent is missing or invalid');
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
      return;
    }

    if (!stripe || !elements) {
      setStripeError('Stripe is still loading. Please try again.');
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
      return;
    }

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setStripeError('Card information is not ready.');
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
      return;
    }

    if (!cardComplete) {
      setStripeError('Please complete card details.');
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
      return;
    }

    const result = await stripe.confirmCardSetup(intent.client_secret, {
      payment_method: {
        card: cardElement,
        billing_details: {
          name: values.name,
          address: {
            line1: values.address,
            city: values.city,
            state: values.state,
            postal_code: values.zip,
            country: 'US',
          },
          phone: values.phone,
        },
      },
    });

    if (result.error) {
      setStripeError(result.error.message ?? null);
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
    } else if (
      result.setupIntent &&
      result.setupIntent.status !== 'succeeded'
    ) {
      Alert.error(ERR_MSG);
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
    } else if (result.setupIntent) {
      onSubmit({
        ...values,
        payment_method: result.setupIntent.payment_method,
        zip: values.zip,
      });
    } else {
      Alert.error(ERR_MSG);
      dispatch({ type: ACTIONS.FAILED_REGISTRATION });
    }
  };
  return (
    <FormContainer mt={15}>
      <UserCard p={30} pt={0} pb={15}>
        <Card.Header textAlign="center">
          <Text fontSize={18}>Enter Credit Card Details</Text>
          <br />
          <Text fontWeight="normal" fontSize={18}>
            One more step! You're almost there!
          </Text>
        </Card.Header>
        <Card.Body mt={10}>
          <Form
            initialValues={{ ...initialValues, country: 'United States' }}
            onSubmit={handleOnSubmit}
            render={({ handleSubmit, submitting, pristine, values }) => (
              <form onSubmit={handleSubmit}>
                <FieldColumn
                  name="name"
                  label="Full Name"
                  validate={required}
                  disabled={loading}
                />
                <FieldColumn
                  name="address"
                  label="Address"
                  validate={required}
                  disabled={loading}
                />
                <FieldColumn
                  name="city"
                  label="City"
                  disabled={loading}
                  validate={required}
                />
                <FieldColumn
                  name="state"
                  label="State"
                  component="select"
                  validate={required}
                  disabled={loading || !values.country}
                  options={values.country ? STATE_OPTIONS(values.country) : []}
                />
                <FieldColumn
                  name="country"
                  label="Country"
                  validate={required}
                  component="select"
                  options={COUNTRY_OPTIONS}
                />
                <FieldColumn
                  name="zip"
                  label="ZIP / Postal Code"
                  validate={required}
                  disabled={loading}
                />
                <FieldColumn
                  name="phone"
                  label="Phone"
                  disabled={loading}
                  validate={composeValidators(required, phoneNumberVal)}
                  transformOnBlur={prettyPhone}
                />

                <Box m="8px 8px 40px 8px">
                  <CardElementContainer>
                    <CardElement
                      options={{ style: cardElementStyles, disabled: loading }}
                      onChange={(event) => {
                        setStripeError(event.error ? event.error.message : null);
                        setCardComplete(event.complete);
                      }}
                    />
                  </CardElementContainer>
                  {stripeError && (
                    <Box>
                      <ErrorMessage fontSize={14} fontWeight={500}>
                        {stripeError}
                      </ErrorMessage>
                    </Box>
                  )}
                  <FinePrint fontSize={12}>
                    *Your card will NOT be charged until after the first 30 days
                  </FinePrint>
                </Box>
                <Button.Group justifyContent="space-between" mt={15}>
                  <BackButton variant="primaryText" onClick={onBackClick}>
                    <FaArrowCircleLeft />
                  </BackButton>
                  <Box width={150}>
                    <Button
                      id="creditCardSubmit"
                      block
                      type="submit"
                      disabled={
                        loading || submitting || pristine || !cardComplete || !stripe
                      }
                    >
                      {loading ? <Spinner size={20} /> : 'Create Account'}
                    </Button>
                  </Box>
                </Button.Group>
              </form>
            )}
          />
        </Card.Body>
      </UserCard>
    </FormContainer>
  );
};

export default CreditCardDetails;
